import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useContent } from "@/context/ContentContext";
import { Reveal } from "@/components/Reveal";

export const PricingSection = ({ full = false }) => {
  const { t } = useLang();
  const { content, L } = useContent();
  const plans = (content?.pricing_plans || []).filter((p) => p.enabled);
  return (
    <section className={`bg-[#191713] py-20 lg:py-32`} id="pricing" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <p className="eyebrow-label mb-6">{t.pricing.eyebrow}</p>
          <h2 className="font-serif text-[#F7F3EC] text-3xl sm:text-4xl lg:text-5xl">{t.pricing.title}</h2>
          <p className="mt-4 text-[#9A9184] max-w-xl">{t.pricing.sub}</p>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {plans.map((plan, i) => (
            <Reveal key={plan.id} delay={i * 0.08}>
              <article
                className={`h-full p-8 lg:p-10 flex flex-col ${
                  plan.featured
                    ? "bg-[#2C2822] border border-[#B8933F]"
                    : "bg-[#23201A] border border-[#B8933F]/20"
                }`}
                data-testid={`pricing-plan-${plan.id}`}
              >
                <div className="flex items-center justify-between">
                  <h3 className="font-serif text-2xl text-[#F7F3EC]">{L(plan.name)}</h3>
                  {L(plan.tag) && (
                    <span className={`text-[0.6rem] tracking-[0.2em] uppercase px-2.5 py-1 ${plan.featured ? "bg-[#B8933F] text-[#191713] font-semibold" : "text-[#9A9184] border border-[#9A9184]/30"}`}>
                      {L(plan.tag)}
                    </span>
                  )}
                </div>
                <div className="mt-6 flex items-baseline gap-2">
                  <span className="font-serif text-5xl text-[#B8933F]">{plan.price}</span>
                  <span className="text-xs text-[#9A9184] tracking-wide">{t.pricing.perMonth}</span>
                </div>
                <p className="mt-4 text-sm text-[#9A9184] leading-relaxed">{L(plan.desc)}</p>
                <div className="mt-6 pt-6 border-t border-[#B8933F]/15 flex-1">
                  {!plan.placeholder && (
                    <ul className="space-y-2.5">
                      {(plan.features || []).map((f, n) => (
                        <li key={n} className="flex items-center gap-2 text-sm text-[#F7F3EC]/85">
                          <span className="w-1 h-1 bg-[#B8933F]/60 shrink-0" aria-hidden="true" /> {L(f)}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <Link to={plan.cta_link || "/contact"} data-testid={`pricing-cta-${plan.id}`} className={`mt-8 ${plan.featured ? "btn-gold" : "btn-outline-light"} w-full`}>
                  {L(plan.cta) || t.pricing.cta}
                </Link>
              </article>
            </Reveal>
          ))}
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          {[t.pricing.oneTime, t.pricing.alacarte].map((box, i) => (
            <Reveal key={i} delay={0.1 + i * 0.08}>
              <div className="bg-[#23201A] border border-[#B8933F]/20 p-8 flex flex-col sm:flex-row sm:items-center justify-between gap-5" data-testid={`pricing-extra-${i}`}>
                <div>
                  <h3 className="font-serif text-xl text-[#F7F3EC]">{box.title}</h3>
                  <p className="mt-2 text-sm text-[#9A9184] max-w-md">{box.body}</p>
                </div>
                <Link to="/contact" className="text-xs tracking-[0.15em] uppercase text-[#B8933F] hover:text-[#C9A959] transition-colors flex items-center gap-2 whitespace-nowrap">
                  {box.cta} <ArrowRight size={14} />
                </Link>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2}>
          <p className="mt-8 text-xs text-[#9A9184]/70 italic">{t.pricing.note}</p>
          {!full && (
            <Link to="/pricing" data-testid="pricing-view-full" className="mt-6 inline-flex items-center gap-2 text-sm text-[#B8933F] hover:text-[#C9A959] transition-colors tracking-wide">
              {t.pricing.viewFull} <ArrowRight size={15} />
            </Link>
          )}
        </Reveal>
      </div>
    </section>
  );
};
